import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for tracking time spent on exercises
 * Automatically starts when component mounts and stops when unmounts or reset
 */
export const useExerciseTimer = () => {
  const [timeSpent, setTimeSpent] = useState(0); // Time in seconds
  const [isRunning, setIsRunning] = useState(true);
  const startTimeRef = useRef(Date.now());
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isRunning) {
      // Update timer every second
      intervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setTimeSpent(elapsed);
      }, 1000);
    }

    // Cleanup on unmount or when stopped
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning]);

  const pause = () => {
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const resume = () => {
    if (!isRunning) {
      startTimeRef.current = Date.now() - (timeSpent * 1000);
      setIsRunning(true);
    }
  };

  const reset = () => {
    setTimeSpent(0);
    startTimeRef.current = Date.now();
    setIsRunning(true);
  };

  const stop = () => {
    pause();
    return timeSpent;
  };

  return {
    timeSpent,
    isRunning,
    pause,
    resume,
    reset,
    stop
  };
};

export default useExerciseTimer;
